import { Drawer } from "@mui/material"
import { useCallback, useEffect, useRef } from "react"
import { NotOnMobile, useMobile } from "lib/@components/mobile"
import { openDialog } from "lib/@hooks/useDialog"
import { Bound } from "lib/@components/binding/Bound"
import { RightDrawerDialog } from "./right-drawer-dialog"

export function RightDrawer({ open, setOpen, keepMounted, sx, dialogActions, children, onClose }) {
    const isMobile = useMobile()
    const isOpenRef = useRef(false)
    const mobileDialogStateRef = useRef(null)
    const mobileDialogState = getMobileDialogState()
    const mobileDialogCancelRef = useRef(null)
    const resetMobileDialog = useCallback(function resetMobileDialog() {
        mobileDialogCancelRef.current = null
        isOpenRef.current = false
    }, [])
    const closeMobileDialog = useCallback(
        function closeMobileDialog() {
            mobileDialogCancelRef.current?.()
            resetMobileDialog()
        },
        [resetMobileDialog]
    )
    const handleSetMobileOpen = useCallback(
        function handleSetMobileOpen(value) {
            setOpen(value)
            if (!value) {
                closeMobileDialog()
            }
        },
        [setOpen, closeMobileDialog]
    )
    const handleOkCancel = useCallback(function handleOkCancel({ cancel }) {
        mobileDialogCancelRef.current = cancel
    }, [])

    useEffect(syncMobileDialogContent, [children, dialogActions, onClose, mobileDialogState])

    useEffect(syncMobileDialog, [
        open,
        isMobile,
        mobileDialogState,
        handleSetMobileOpen,
        handleOkCancel,
        closeMobileDialog,
        resetMobileDialog,
    ])

    return (
        <Bound close={() => setOpen(false)}>
            <NotOnMobile>
                <Drawer
                    keepMounted={keepMounted}
                    sx={{
                        "& .MuiDrawer-paper": {
                            width: 450,
                            padding: 2,
                            margin: 3,
                            height: "calc(100% - 48px)",
                            ...sx,
                        },
                    }}
                    anchor="right"
                    open={open}
                    onClose={() => {
                        setOpen(false)
                    }}
                    slotProps={{ backdrop: { "data-cy": "right-drawer-backdrop" } }}
                    PaperProps={{ square: false }}
                    data-cy="rightDrawer"
                >
                    {children}
                </Drawer>
            </NotOnMobile>
        </Bound>
    )

    function syncMobileDialog() {
        if (open && isMobile && !isOpenRef.current) {
            openDialog(<RightDrawerDialog setOpen={handleSetMobileOpen} state={mobileDialogState} />, {
                fullScreen: true,
                onOkCancel: handleOkCancel,
            })
                .catch(console.error)
                .finally(resetMobileDialog)
            isOpenRef.current = true
            return
        }

        if (isOpenRef.current && (!open || !isMobile)) {
            closeMobileDialog()
        }
    }

    function syncMobileDialogContent() {
        mobileDialogState.setContent({ children, dialogActions, onClose })
    }

    function getMobileDialogState() {
        if (!mobileDialogStateRef.current) {
            mobileDialogStateRef.current = createMobileDialogState()
        }
        return mobileDialogStateRef.current
    }

    function createMobileDialogState() {
        const state = {
            content: { children, dialogActions, onClose },
            listeners: new Set(),
            version: 0,
            subscribe(listener) {
                state.listeners.add(listener)
                return function unsubscribe() {
                    state.listeners.delete(listener)
                }
            },
            getSnapshot() {
                return state.version
            },
            setContent(content) {
                state.content = content
                state.version += 1
                for (const listener of state.listeners) {
                    listener()
                }
            },
        }
        return state
    }
}
